import { fieldsApi, type FieldsResponse, flattenStreams } from 'api-validations/pangea'
import { useCallback } from 'react'
import { isMatch, uniqueBy } from 'utils'

import { useOrgCodeOrThrow } from '../active-operation'
import {
  type ApiClient,
  panoramaQueryOptions,
  toQuery,
  useApiClient,
  useQuery
} from '../api-provider'
import { useLocalization } from '../localization'
import { useLiveStreamData } from './EmissaryWebsocketProvider'
import { toAllStreams } from './streamCatalog'

export type UnlabeledStream = ReturnType<typeof flattenStreams>[number]

export type Stream = UnlabeledStream & {
  indexLabel?: string
  cropLabel: string
}

const query = (orgCode: string, apiClient: ApiClient) =>
  toQuery({
    ...panoramaQueryOptions(fieldsApi, { apiClient, params: { orgCode } }),
    select: (data: FieldsResponse) => flattenStreams(data)
  })

const EMPTY_CRITERIA: Partial<Stream> = {}

/**
 * Get all streams for the active operation, including live streams.
 */
export const useAllStreams = (searchCriteria: Partial<Stream> = EMPTY_CRITERIA) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const liveStreams = useLiveStreamData((d) => d.liveAccountConfig)
  const { t } = useLocalization()

  const select = useCallback(
    (data: FieldsResponse) =>
      toAllStreams({ data, liveStreams, t }).filter((stream) => isMatch(stream, searchCriteria)),
    [searchCriteria, liveStreams, t]
  )

  return useQuery({
    ...query(orgCode, apiClient),
    select
  })
}

export const useAllStreamsForOrgAsAdmin = (orgCode: string) => {
  const apiClient = useApiClient()

  return useQuery(query(orgCode, apiClient))
}

export const useAllStreamsAsCffs = () => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()

  return useQuery({
    ...query(orgCode, apiClient),
    select: (data) =>
      data
        .flatMap((dbm) =>
          dbm.fields.map((field) => ({
            client: field.client,
            farm: field.farm,
            field: field.field
          }))
        )
        .filter(
          uniqueBy((a, b) => a.client === b.client && a.farm === b.farm && a.field === b.field)
        )
  })
}
